import { setStyle } from '../ui/dom.ts';

/**
 * The paths' marks over the board (the tappable rings and pills of every chapter's HUD) and the name tags over the
 * heroes (and the life bars over the path's people): a mark must never sit on a name tag. Three chapters running a
 * mark landed on the hero's own name on a phone (the rock's pill, the windbreak's pull, the large wedges' arrow), each
 * fixed in its own HUD; this is the fix for all of them. Every HUD pins its marks through `pinMark` (the screen point
 * it wants, centred); after they are all pinned, `clearOfTags` moves any that overlaps a tag up or down, the shorter
 * way, just clear of it. Sizes are read once per element (no layout read per frame), and again only when a HUD says a
 * mark's words changed (`remeasureMark`).
 *
 * A mark's box is its ring and every label hung off it (a gate's name above its ring, its state below): measured as
 * the ring alone, the monastery's «Huertas» sat on the hero's name.
 */

interface At {
  x: number;
  y: number;
  /** The box round the pinned point (px): left, top, right, bottom offsets (left and top negative). */
  l: number;
  t: number;
  r: number;
  b: number;
  /** Measured (false: read on the next pin). */
  ok: boolean;
  /** Where its centre ended up this frame, after `clearOfTags` (the bubbles keep clear of it: `markBoxes`). */
  fy: number;
}

const AT = new WeakMap<Element, At>();

/** A tag's box on screen (px), left, top, right, bottom. */
export interface TagBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** The tag itself (a hero's name): it goes thin while a mark sits under it, so the piece the mark names shows. */
  el?: HTMLElement;
}

const place = (el: HTMLElement, x: number, y: number): void => setStyle(el, 'transform', `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%)`);

/** The mark's box with its labels, about its centre (one layout read, on its first pin or after a change). */
const measure = (el: HTMLElement, a: At): void => {
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  if (!w) return;
  a.l = -w / 2;
  a.t = -h / 2;
  a.r = w / 2;
  a.b = h / 2;
  a.ok = true;
  const kids = el.children;
  if (!kids.length) return;
  // (The labels hang off the ring with transforms of their own: their rects against the ring's say where they are.)
  const box = el.getBoundingClientRect();
  const cx = box.left + box.width / 2;
  const cy = box.top + box.height / 2;
  for (let i = 0; i < kids.length; i++) {
    const r = (kids[i] as Element).getBoundingClientRect();
    if (!r.width || !r.height) continue;
    a.l = Math.min(a.l, r.left - cx);
    a.t = Math.min(a.t, r.top - cy);
    a.r = Math.max(a.r, r.right - cx);
    a.b = Math.max(a.b, r.bottom - cy);
  }
};

/** A mark centred at this screen point (and remembered there for `clearOfTags`). */
export const pinMark = (el: HTMLElement, x: number, y: number): void => {
  let a = AT.get(el);
  if (!a) {
    a = { x, y, l: -22, t: -22, r: 22, b: 22, ok: false, fy: y };
    AT.set(el, a);
  }
  a.x = x;
  a.y = y;
  a.fy = y;
  place(el, x, y);
  if (!a.ok) measure(el, a);
};

/** A mark's words changed (a state shown, hidden or longer): its box is read again on its next pin. */
export const remeasureMark = (el: HTMLElement): void => {
  const a = AT.get(el);
  if (a) a.ok = false;
};

/** Moves the marks under `root` (pinned with `pinMark`, shown) off every tag box they overlap. */
export const clearOfTags = (root: HTMLElement, boxes: readonly TagBox[]): void => {
  if (!boxes.length) return;
  // (By index, no array per frame: this runs every frame over every mark.)
  const kids = root.children;
  // (A clear gap, not a touch: a name just under a tag still read as stuck to it.)
  const pad = 7;
  // The tags a mark had to move off (a bit each, the first 31): on the chapter 1 gears the hero's name stood on the
  // gear he was about to take, and the mark moved off left the gear itself under the name.
  let hit = 0;
  for (let i = 0; i < kids.length; i++) {
    const el = kids[i] as HTMLElement;
    const a = AT.get(el);
    if (!a || el.hidden || el.style.display === 'none') continue;
    let y = a.fy;
    let moved = false;
    for (let bi = 0; bi < boxes.length; bi++) {
      const b = boxes[bi] as TagBox;
      if (a.x + a.r < b.x0 - pad || a.x + a.l > b.x1 + pad || y + a.b < b.y0 - pad || y + a.t > b.y1 + pad) continue;
      if (bi < 31) hit |= 1 << bi;
      // Up: the whole box (its name too) just over the tag; down: just under it. The shorter way.
      const up = b.y0 - pad - a.b;
      const down = b.y1 + pad - a.t;
      y = Math.abs(up - y) <= Math.abs(down - y) ? up : down;
      moved = true;
    }
    if (moved) {
      a.fy = y;
      place(el, a.x, y);
    }
  }
  for (let bi = 0; bi < boxes.length && bi < 31; bi++) {
    const t = (boxes[bi] as TagBox).el;
    if (t && t.classList.contains('thin') !== ((hit & (1 << bi)) !== 0)) t.classList.toggle('thin');
  }
};

/**
 * Moves the marks under `root` off each other: one that overlaps a mark before it (in the root's order) goes up or
 * down, the shorter way, just clear of it. On a phone the frozen mirrors' five mirrors and six bases sat on one another
 * and a base's «lit» covered two mirrors' numbers (clerigo 3); this is the fix for every path. Call it before
 * `clearOfTags` (which starts from where this left them). A dozen marks: a few dozen box checks a frame.
 */
export const spreadMarks = (root: HTMLElement): void => {
  const kids = root.children;
  const pad = 3;
  for (let i = 1; i < kids.length; i++) {
    const el = kids[i] as HTMLElement;
    const a = AT.get(el);
    if (!a || !a.ok || el.hidden || el.style.display === 'none') continue;
    let y = a.fy;
    let moved = false;
    for (let pass = 0, hit = true; hit && pass < 3; pass++) {
      hit = false;
      for (let j = 0; j < i; j++) {
        const oe = kids[j] as HTMLElement;
        const o = AT.get(oe);
        if (!o || !o.ok || oe.hidden || oe.style.display === 'none') continue;
        if (a.x + a.r < o.x + o.l - pad || a.x + a.l > o.x + o.r + pad || y + a.b < o.fy + o.t - pad || y + a.t > o.fy + o.b + pad) continue;
        const up = o.fy + o.t - pad - a.b;
        const down = o.fy + o.b + pad - a.t;
        y = Math.abs(up - y) <= Math.abs(down - y) ? up : down;
        hit = moved = true;
      }
    }
    if (moved) {
      a.fy = y;
      place(el, a.x, y);
    }
  }
};

/**
 * The boxes of the marks under `root` as they stand this frame (after `clearOfTags`), pushed onto `out`: the people's
 * bubbles keep clear of them as they do of names. On the chapels' bridge a bubble said by the stretcher sat on its
 * «Go / Wait» mark just when it had to be tapped (clerigo 2, phone); this is the fix for every path.
 */
export const markBoxes = (root: HTMLElement, out: TagBox[]): void => {
  const kids = root.children;
  for (let i = 0; i < kids.length; i++) {
    const el = kids[i] as HTMLElement;
    const a = AT.get(el);
    if (!a || !a.ok || el.hidden || el.style.display === 'none') continue;
    out.push({ x0: a.x + a.l, y0: a.fy + a.t, x1: a.x + a.r, y1: a.fy + a.b });
  }
};
